import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Reveal } from "@/components/demo/lib/Reveal";
import { IMG } from "@/components/demo/lib/images";

const WA = "41765008176";

const ORIGINS = [
  { id: "yir", name: "Etiopia Yirgacheffe", short: "Yirgacheffe", notes: ["floreale", "bergamotto", "pesca"], sca: 89, price: 24, img: IMG.coffee.beans1 },
  { id: "hel", name: "Blend Helvetia", short: "Helvetia", notes: ["cioccolato", "mandorla", "caramello"], sca: 86, price: 22, featured: true, img: IMG.coffee.beans2 },
  { id: "col", name: "Colombia La Esperanza", short: "La Esperanza", notes: ["miele", "lampone", "latte"], sca: 87, price: 21, img: IMG.coffee.beans3 },
  { id: "geisha", name: "Panama Geisha", short: "Geisha", notes: ["gelsomino", "lychee", "tè"], sca: 92, price: 48, img: IMG.coffee.beans4 },
];

const ROASTS = [
  { id: "light", label: "Chiara", desc: "Per filtro, V60, Aeropress" },
  { id: "med", label: "Media", desc: "Espresso e filtro" },
  { id: "dark", label: "Scura", desc: "Espresso classico italiano" },
];

const GRIND = [
  { id: "whole", label: "In grani" },
  { id: "esp", label: "Macinato espresso" },
  { id: "filter", label: "Macinato filtro" },
  { id: "french", label: "Macinato French press" },
];

const PLANS = [
  { id: "trial", label: "Una tantum", desc: "Spedizione singola", mult: 1 },
  { id: "month", label: "Abbonamento mensile", desc: "Sconto del 10%", mult: 0.9, save: true },
  { id: "week2", label: "Ogni 2 settimane", desc: "Sconto del 15%", mult: 0.85, save: true },
];

function Helvetia() {
  const [origin, setOrigin] = useState(ORIGINS[0]);
  const [roast, setRoast] = useState(ROASTS[1]);
  const [grind, setGrind] = useState(GRIND[0]);
  const [plan, setPlan] = useState(PLANS[1]);
  const [qty, setQty] = useState(1);

  const unit = useMemo(() => origin.price * plan.mult, [origin, plan]);
  const total = useMemo(() => unit * qty, [unit, qty]);

  return (
    <div className="min-h-screen bg-[#faf7f0] text-stone-900 antialiased overflow-x-hidden">
      <style>{`.ff-display { font-family: 'Cormorant Garamond', 'Times New Roman', serif } body { font-family: 'Inter', sans-serif }`}</style>

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#faf7f0]/85 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <div className="ff-display text-2xl">Helvetia <span className="text-amber-700">·</span> Coffee</div>
          <nav className="hidden md:flex gap-8 text-sm text-stone-600">
            <a href="#shop" className="hover:text-amber-700">Shop</a>
            <a href="#config" className="hover:text-amber-700">Configura</a>
            <a href="#story" className="hover:text-amber-700">La nostra storia</a>
            <a href="#loyalty" className="hover:text-amber-700">Helvetia Club</a>
          </nav>
          <a href="#config" className="bg-stone-900 text-[#faf7f0] hover:bg-amber-700 px-5 py-2.5 text-sm rounded-full transition">Ordina ora</a>
        </div>
      </header>

      {/* HERO */}
      <section className="px-6 sm:px-10 pt-16 pb-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[3fr_2fr] gap-12 items-end">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-amber-700 mb-6">— Torrefazione di specialità · Mendrisio</div>
            <h1 className="ff-display text-6xl sm:text-8xl lg:text-[8vw] leading-[0.92] break-words">
              Caffè<br />di specialità,<br /><em>tostato a mano.</em>
            </h1>
            <p className="mt-10 text-stone-600 max-w-md leading-relaxed text-lg">
              Microlot single-origin con punteggio SCA 86+. Tostati in piccoli batch a Mendrisio e spediti freschi a casa tua, ogni mese.
            </p>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-stone-500">
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-amber-700" /> Direct trade</div>
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-amber-700" /> Tostato in 24 ore</div>
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-amber-700" /> Spedizione gratuita oltre CHF 50</div>
            </div>
          </div>
          <Reveal delay={120}>
            <div className="aspect-[4/5] relative rounded-3xl overflow-hidden bg-stone-100">
              <img src={IMG.coffee.hero} alt="Sacchi di caffè Helvetia in torrefazione" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute bottom-6 left-6 right-6 backdrop-blur-md bg-white/70 rounded-2xl p-4">
                <div className="text-[10px] tracking-[0.3em] uppercase text-amber-700">Più venduto</div>
                <div className="ff-display text-2xl mt-1">Blend Helvetia</div>
                <div className="text-sm text-stone-600 mt-1">SCA 86 · CHF 22 / 250g</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SHOP */}
      <section id="shop" className="px-6 sm:px-10 py-20 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="ff-display text-4xl lg:text-6xl mb-12 break-words">Le nostre origini.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ORIGINS.map((o, i) => (
              <Reveal key={o.id} delay={i * 50}>
                <button onClick={() => setOrigin(o)} className={`text-left block w-full p-6 rounded-3xl border-2 transition ${origin.id === o.id ? "border-amber-700 bg-amber-50" : "border-stone-200 bg-white hover:border-amber-400"}`}>
                  <div className="aspect-square bg-stone-100 rounded-2xl mb-5 overflow-hidden">
                    <img src={o.img} alt={`${o.name} — caffè in grani`} className="w-full h-full object-cover hover:scale-105 transition duration-700" />
                  </div>
                  {o.featured && <div className="text-[10px] tracking-widest uppercase text-amber-700 mb-1">★ Signature</div>}
                  <div className="ff-display text-2xl">{o.name}</div>
                  <div className="text-xs text-stone-500 mt-2">Punteggio SCA {o.sca}</div>
                  <div className="flex gap-1 flex-wrap mt-3">{o.notes.map(n => <span key={n} className="text-[10px] uppercase tracking-wider bg-stone-100 px-2 py-0.5 rounded-full">{n}</span>)}</div>
                  <div className="ff-display text-xl mt-4">CHF {o.price}</div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONFIGURATOR */}
      <section id="config" className="px-6 sm:px-10 py-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[5fr_4fr] gap-12">
          <Reveal>
            <div className="sticky top-28 self-start">
              <div className="text-[10px] tracking-[0.4em] uppercase text-amber-700 mb-3">Configura il tuo caffè</div>
              <h2 className="ff-display text-5xl lg:text-7xl leading-tight break-words">Componi la tua scorta.</h2>
              <div className="mt-10 aspect-square rounded-3xl bg-gradient-to-br from-amber-100 to-stone-100 overflow-hidden relative">
                <img src={origin.img} alt={origin.name} className="w-full h-full object-cover" />
                <div className="absolute bottom-6 left-6 right-6 backdrop-blur-md bg-white/80 rounded-2xl p-5">
                  <div className="ff-display text-2xl">{origin.name}</div>
                  <div className="text-sm text-stone-600 mt-1">Tostatura {roast.label.toLowerCase()} · {grind.label}</div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="ff-display text-4xl">CHF {total.toFixed(2)}</span>
                    {plan.save && <span className="text-xs text-amber-700 line-through">CHF {(origin.price * qty).toFixed(2)}</span>}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <div className="space-y-8">
            <div>
              <div className="text-xs tracking-widest uppercase text-stone-500 mb-3">1 · Origine</div>
              <div className="flex flex-wrap gap-2">{ORIGINS.map(o => <button key={o.id} onClick={() => setOrigin(o)} className={`px-4 py-2.5 rounded-full text-sm border transition ${origin.id === o.id ? "bg-stone-900 text-white border-stone-900" : "border-stone-200 hover:border-stone-900"}`}>{o.short}</button>)}</div>
            </div>
            <div>
              <div className="text-xs tracking-widest uppercase text-stone-500 mb-3">2 · Tostatura</div>
              <div className="grid grid-cols-3 gap-2">
                {ROASTS.map(r => (
                  <button key={r.id} onClick={() => setRoast(r)} className={`p-4 rounded-2xl border text-left transition ${roast.id === r.id ? "border-amber-700 bg-amber-50" : "border-stone-200 hover:border-amber-400"}`}>
                    <div className="font-medium">{r.label}</div>
                    <div className="text-xs text-stone-500 mt-1">{r.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs tracking-widest uppercase text-stone-500 mb-3">3 · Macinatura</div>
              <div className="grid grid-cols-2 gap-2">
                {GRIND.map(g => (
                  <button key={g.id} onClick={() => setGrind(g)} className={`p-3 rounded-2xl border text-sm transition ${grind.id === g.id ? "border-stone-900 bg-stone-900 text-white" : "border-stone-200 hover:border-stone-900"}`}>{g.label}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs tracking-widest uppercase text-stone-500 mb-3">4 · Frequenza</div>
              <div className="grid grid-cols-3 gap-2">
                {PLANS.map(p => (
                  <button key={p.id} onClick={() => setPlan(p)} className={`p-4 rounded-2xl border text-left transition ${plan.id === p.id ? "border-amber-700 bg-amber-50" : "border-stone-200 hover:border-amber-400"}`}>
                    <div className="font-medium">{p.label}</div>
                    <div className="text-xs text-stone-500 mt-1">{p.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs tracking-widest uppercase text-stone-500 mb-3">5 · Quantità</div>
              <div className="inline-flex items-center border border-stone-300 rounded-full overflow-hidden">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-5 py-3 hover:bg-stone-100">−</button>
                <div className="px-6 font-medium tabular-nums">{qty} × 250g</div>
                <button onClick={() => setQty(Math.min(12, qty + 1))} className="px-5 py-3 hover:bg-stone-100">+</button>
              </div>
            </div>

            <a href={`https://wa.me/${WA}?text=${encodeURIComponent(`Ciao Helvetia, vorrei ordinare:\n• ${origin.name}\n• Tostatura: ${roast.label}\n• Macinatura: ${grind.label}\n• Piano: ${plan.label}\n• Quantità: ${qty} × 250g\n• Totale: CHF ${total.toFixed(2)}`)}`} className="block text-center bg-stone-900 text-white py-5 rounded-full text-sm font-medium hover:bg-amber-700 transition">Ordina su WhatsApp — CHF {total.toFixed(2)}</a>
            <div className="text-xs text-stone-500 text-center">Spedizione gratuita oltre CHF 50 · Consegna in Svizzera in 24–48 ore</div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section id="story" className="px-6 sm:px-10 py-24 bg-stone-900 text-stone-100">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-[10px] tracking-[0.4em] uppercase text-amber-400 mb-4">— La nostra storia</div>
            <h2 className="ff-display text-4xl lg:text-6xl leading-tight max-w-3xl break-words">
              Dal 2018 importiamo solo lotti che <em className="text-amber-400">supereremmo a un cupping cieco</em>. È l'unico filtro che usiamo per scegliere.
            </h2>
          </Reveal>
          <div className="mt-16 grid sm:grid-cols-3 gap-px bg-white/10">
            {[
              { v: "86+", l: "Punteggio SCA minimo" },
              { v: "12 kg", l: "Batch di tostatura artigianale" },
              { v: "Direct", l: "Trade diretto con i produttori" },
            ].map(s => (
              <div key={s.l} className="bg-stone-900 p-8">
                <div className="ff-display text-5xl text-amber-400">{s.v}</div>
                <div className="text-xs uppercase tracking-widest text-white/50 mt-3">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="px-6 sm:px-10 py-24">
        <div className="max-w-4xl mx-auto text-center">
          <div className="ff-display text-9xl text-amber-700/20 leading-none">"</div>
          <blockquote className="ff-display text-2xl lg:text-4xl leading-snug -mt-6 text-stone-800 break-words">
            Sono passato da Helvetia tre anni fa e non sono più tornato indietro. <em className="text-stone-500">Il blend Helvetia in tostatura media è quello che servo ai miei clienti al ristorante: equilibrio, dolcezza, mai amaro.</em>
          </blockquote>
          <div className="mt-8 text-xs tracking-widest uppercase text-stone-500">— Luca M. · Chef, Locarno</div>
        </div>
      </section>

      {/* LOYALTY */}
      <section id="loyalty" className="px-6 sm:px-10 pb-24">
        <div className="max-w-6xl mx-auto bg-amber-50 rounded-3xl p-10 sm:p-16 border border-amber-100">
          <Reveal>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-amber-700 mb-4">Helvetia Club</div>
                <h3 className="ff-display text-4xl lg:text-5xl leading-tight break-words">Ogni 10 sacchi, uno in regalo.</h3>
                <p className="text-stone-600 mt-4">Più accesso anticipato ai microlot in edizione limitata e invito ai cupping del sabato in torrefazione a Mendrisio.</p>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className={`aspect-square rounded-full grid place-items-center text-xs font-medium ${i < 6 ? "bg-amber-700 text-white" : "bg-white border border-amber-200 text-stone-400"}`}>{i + 1}</div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="px-6 sm:px-10 py-10 border-t border-stone-200 text-xs text-stone-500">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between gap-4">
          <div>Helvetia Coffee · Torrefazione, Via Industria 14, 6850 Mendrisio · +41 91 630 22 18</div>
          <div>Demo FlashWeb · <Link to="/" className="underline">flash-web.ch</Link></div>
        </div>
      </footer>
    </div>
  );
}

export const Route = createFileRoute("/demo/helvetia-coffee")({
  head: () => ({
    meta: [
      { title: "Helvetia Coffee — Caffè di specialità a Mendrisio (Demo FlashWeb)" },
      { name: "description", content: "Caffè di specialità single-origin tostato a Mendrisio. Abbonamento mensile, configurazione su misura e spedizione gratuita in Svizzera oltre CHF 50." },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: Helvetia,
});
